/* FAQ management page in the shared admin page frame with CRUD operations. */



import { useState, useEffect } from 'react';

import Breadcrumb from '@/components/admin/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, CreditCard as Edit2, Trash2, ChevronUp, ChevronDown, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, CircleHelp as HelpCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  display_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

const categories = [
  { value: 'algemeen', label: 'Algemeen' },
  { value: 'verzending', label: 'Verzending' },
  { value: 'planten', label: 'Planten' },
  { value: 'bestelling', label: 'Bestelling' },
  { value: 'onderhoud', label: 'Onderhoud' }
];

export default function FAQPage() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedFaq, setSelectedFaq] = useState<FAQ | null>(null);
  const [faqToDelete, setFaqToDelete] = useState<FAQ | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'algemeen',
    display_order: 0,
    active: true
  });

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    if (categoryFilter === 'all') {
      setFilteredFaqs(faqs);
    } else {
      setFilteredFaqs(faqs.filter(faq => faq.category === categoryFilter));
    }
  }, [categoryFilter, faqs]);

  const fetchFaqs = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setFaqs(data || []);
      setFilteredFaqs(data || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setMessage({ type: 'error', text: 'Fout bij het ophalen van FAQs' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (faq?: FAQ) => {
    if (faq) {
      setSelectedFaq(faq);
      setFormData({
        question: faq.question,
        answer: faq.answer,
        category: faq.category,
        display_order: faq.display_order,
        active: faq.active
      });
    } else {
      setSelectedFaq(null);
      setFormData({
        question: '',
        answer: '',
        category: 'algemeen',
        display_order: faqs.length,
        active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedFaq(null);
    setFormData({
      question: '',
      answer: '',
      category: 'algemeen',
      display_order: 0,
      active: true
    });
  };

  const handleSaveFaq = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      setMessage({ type: 'error', text: 'Vraag en antwoord zijn verplicht' });
      return;
    }

    try {
      if (selectedFaq) {
        const { error } = await supabase
          .from('faqs')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedFaq.id);

        if (error) throw error;
        setMessage({ type: 'success', text: 'FAQ succesvol bijgewerkt' });
      } else {
        const { error } = await supabase
          .from('faqs')
          .insert([formData]);

        if (error) throw error;
        setMessage({ type: 'success', text: 'FAQ succesvol toegevoegd' });
      }

      handleCloseDialog();
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      setMessage({ type: 'error', text: 'Fout bij het opslaan van FAQ' });
    }
  };

  const handleDeleteFaq = async () => {
    if (!faqToDelete) return;

    try {
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', faqToDelete.id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'FAQ succesvol verwijderd' });
      setIsDeleteDialogOpen(false);
      setFaqToDelete(null);
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      setMessage({ type: 'error', text: 'Fout bij het verwijderen van FAQ' });
    }
  };

  const handleToggleActive = async (faq: FAQ) => {
    try {
      const { error } = await supabase
        .from('faqs')
        .update({
          active: !faq.active,
          updated_at: new Date().toISOString()
        })
        .eq('id', faq.id);

      if (error) throw error;
      fetchFaqs();
    } catch (error) {
      console.error('Error toggling FAQ status:', error);
      setMessage({ type: 'error', text: 'Fout bij het wijzigen van status' });
    }
  };

  const handleMoveOrder = async (faq: FAQ, direction: 'up' | 'down') => {
    const currentIndex = filteredFaqs.findIndex(f => f.id === faq.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === filteredFaqs.length - 1)
    ) {
      return;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const targetFaq = filteredFaqs[targetIndex];

    try {
      const updates = [
        supabase.from('faqs').update({ display_order: targetFaq.display_order }).eq('id', faq.id),
        supabase.from('faqs').update({ display_order: faq.display_order }).eq('id', targetFaq.id)
      ];

      await Promise.all(updates);
      fetchFaqs();
    } catch (error) {
      console.error('Error reordering FAQs:', error);
      setMessage({ type: 'error', text: 'Fout bij het herordenen van FAQs' });
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#1e2b22] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Laden...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Veelgestelde vragen" />

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-jv-primary mb-2">Veelgestelde vragen</h1>
            <p className="text-gray-600">Beheer FAQ items voor de website</p>
          </div>
          <Button
            onClick={() => handleOpenDialog()}
            className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nieuwe FAQ
          </Button>
        </div>

        {message && (
          <Alert
            variant={message.type === 'error' ? 'destructive' : 'default'}
            className="mb-6"
          >
            {message.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        <div className="mb-6">
          <Label htmlFor="category-filter">Filter op categorie</Label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger id="category-filter" className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle categorieën</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4">
          {filteredFaqs.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <HelpCircle className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg mb-2">Geen FAQs gevonden</p>
                <p className="text-gray-400">Klik op "Nieuwe FAQ" om een vraag toe te voegen</p>
              </CardContent>
            </Card>
          ) : (
            filteredFaqs.map((faq, index) => (
              <Card key={faq.id} className={!faq.active ? 'opacity-60' : ''}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 text-xs font-semibold rounded" style={{ backgroundColor: '#ffaf2e', color: '#153907' }}>
                          {categories.find(c => c.value === faq.category)?.label}
                        </span>
                        {!faq.active && (
                          <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-200 text-gray-600">
                            INACTIEF
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-xl">{faq.question}</CardTitle>
                      <CardDescription className="mt-2">{faq.answer}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMoveOrder(faq, 'up')}
                          disabled={index === 0}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleMoveOrder(faq, 'down')}
                          disabled={index === filteredFaqs.length - 1}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={faq.active}
                            onCheckedChange={() => handleToggleActive(faq)}
                          />
                          <Label className="text-xs">Actief</Label>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleOpenDialog(faq)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setFaqToDelete(faq);
                            setIsDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))
          )}
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedFaq ? 'FAQ bewerken' : 'Nieuwe FAQ toevoegen'}</DialogTitle>
              <DialogDescription>
                Vul de vraag en het antwoord in voor de FAQ sectie
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="question">Vraag *</Label>
                <Input
                  id="question"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="Bijvoorbeeld: Hoe verzorg ik mijn haagplanten?"
                />
              </div>

              <div>
                <Label htmlFor="answer">Antwoord *</Label>
                <Textarea
                  id="answer"
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  placeholder="Geef een duidelijk antwoord op de vraag..."
                  rows={5}
                />
              </div>

              <div>
                <Label htmlFor="category">Categorie</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger id="category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <Label htmlFor="active">Actief (zichtbaar op website)</Label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Annuleren
              </Button>
              <Button onClick={handleSaveFaq} className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white">
                {selectedFaq ? 'Opslaan' : 'Toevoegen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>FAQ verwijderen</DialogTitle>
              <DialogDescription>
                Weet je zeker dat je deze FAQ wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
              </DialogDescription>
            </DialogHeader>
            {faqToDelete && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold">{faqToDelete.question}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Annuleren
              </Button>
              <Button variant="destructive" onClick={handleDeleteFaq}>
                Verwijderen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
